import type { MembershipAchievementForMember, MembershipAchievementType } from "@/pages/people/[pgaId]/queries";
import type { DeleteAchievementMutations, DeleteTarget, DeleteTargetResolution } from "./types";

export function resolveDeleteTarget(
  achievement: MembershipAchievementForMember,
  achievementType: MembershipAchievementType | undefined
): DeleteTargetResolution {
  if (!achievementType) {
    return { target: null, reason: "Achievement type could not be resolved." };
  }

  if (achievementType.native) {
    return {
      reason: null,
      target: {
        kind: "native",
        input: {
          id: achievement.id
        }
      }
    };
  }

  const code = achievement.code.code?.trim();
  if (!code) {
    return { target: null, reason: "Achievement is missing a code." };
  }

  if (achievementType.ccmsCodeType) {
    if (!achievement.recognizedAt) {
      return { target: null, reason: "Achievement is missing a recognition date." };
    }

    return {
      reason: null,
      target: {
        kind: "code",
        input: {
          code,
          codeType: achievementType.ccmsCodeType,
          asofDate: achievement.recognizedAt
        }
      }
    };
  }

  if (!achievementType.ccmsAttributeType) {
    return { target: null, reason: "This achievement type cannot be deleted." };
  }

  return {
    reason: null,
    target: {
      kind: "attribute",
      input: {
        attributeType: achievementType.ccmsAttributeType,
        attributeCode: code
      }
    }
  };
}

export async function deleteAchievement({
  mutations,
  pgaId,
  target
}: {
  mutations: DeleteAchievementMutations;
  pgaId: string;
  target: DeleteTarget;
}): Promise<{ message?: string | null; success: boolean }> {
  if (target.kind === "native") {
    const { data } = await mutations.deleteNativeAchievement({
      variables: {
        input: {
          pgaId,
          ...target.input
        }
      }
    });

    return {
      success: Boolean(data?.deleteNativeAchievement.success),
      message: data?.deleteNativeAchievement.message
    };
  }

  if (target.kind === "code") {
    const { data } = await mutations.deleteAchievementCode({
      variables: {
        input: {
          pgaId,
          ...target.input
        }
      }
    });

    return {
      success: Boolean(data?.deleteAchievementCode.success),
      message: data?.deleteAchievementCode.message
    };
  }

  const { data } = await mutations.deleteAchievementAttribute({
    variables: {
      input: {
        pgaId,
        ...target.input
      }
    }
  });

  return {
    success: Boolean(data?.deleteAchievementAttribute.success),
    message: data?.deleteAchievementAttribute.message
  };
}
